import React, { useId, useState } from "react";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent
} from "../ui/collapsible";
import { ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";

interface CollapsibleSectionProps {
  title: string;
  badge?: string;
  summary?: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

export const CollapsibleSection = ({
  title,
  badge,
  summary,
  defaultExpanded = false,
  children
}: CollapsibleSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultExpanded);
  const contentId = useId();

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="collapsible-section border border-border rounded-lg bg-[var(--panelBackground)]">
        <CollapsibleTrigger
          aria-controls={contentId}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronRight
            className={cn(
              "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
              isOpen && "rotate-90"
            )}
            aria-hidden="true"
          />
          <span className="text-sm font-bold text-[var(--accent-color)]">
            {title}
          </span>
          <span className="sr-only">
            {isOpen ? "(expanded)" : "(collapsed)"}
          </span>
          {badge && (
            <span className="rounded-full bg-[var(--accent-color,theme(colors.teal.500))]/10 px-2 py-0.5 text-xs font-bold text-[var(--accent-color,theme(colors.teal.500))] border border-[var(--accent-color,theme(colors.teal.500))]/25">
              {badge}
            </span>
          )}
          <span className="flex-1" />
          {!isOpen && summary && (
            <span className="max-w-[240px] truncate text-xs text-muted-foreground">
              {summary}
            </span>
          )}
        </CollapsibleTrigger>
        <CollapsibleContent id={contentId}>
          <div className="border-t border-border px-3 pb-3 pt-2.5">
            {children}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
